import { Print } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateAttender } from '../../../redux/Attender/attenderAction';

const PrintAttenderDialog = ({
  open,
  onClose,
  selectedPatient,
  selectedAttenders,
  setSelectedAttenders,
  handlePrintSelected,
}) => {
  const dispatch = useDispatch();
  const [loadingAttenderId, setLoadingAttenderId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Local state to track status changes before Redux updates
  const [localAttenderStatus, setLocalAttenderStatus] = useState({});

  const handleStatusToggle = async (attenderId, currentStatus) => {
    setLoadingAttenderId(attenderId);
    const newStatus = currentStatus === 1 ? 3 : 1; // Toggle between 1 (active) and 3 (inactive)
    
    try {
      // Update local state immediately for UI feedback
      setLocalAttenderStatus(prev => ({
        ...prev,
        [attenderId]: newStatus
      }));
      
      // Dispatch action but don't store the entire response in Redux
      await dispatch(updateAttender(attenderId, { attender_status: newStatus.toString() }));
      
      setSnackbar({
        open: true,
        message: `Attendant pass updated to ${newStatus === 1 ? 'Active (1)' : 'Inactive (3)'}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating attendant status:', error);
      
      // Reset local state on error
      setLocalAttenderStatus(prev => ({
        ...prev,
        [attenderId]: currentStatus
      }));
      
      setSnackbar({
        open: true,
        message: 'Failed to update attendant status',
        severity: 'error',
      });
    } finally {
      setLoadingAttenderId(null);
    }
  };

  const getRelationLabel = (relationValue) => {
    const relationOptions = [
      { value: '1', label: 'Father' },
      { value: '2', label: 'Mother' },
      { value: '3', label: 'Spouse' },
      { value: '4', label: 'Child' },
      { value: '5', label: 'Sibling' },
      { value: '6', label: 'Other' },
    ];
    
    return (
      relationOptions.find(
        (opt) =>
          opt.value === relationValue || opt.value === String(relationValue)
      )?.label || 'Unknown'
    );
  };

  // Get effective status (either from local state if modified, or from original data)
  const getEffectiveStatus = (attenderId, originalStatus) => {
    return localAttenderStatus[attenderId] !== undefined 
      ? localAttenderStatus[attenderId] 
      : originalStatus;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: '#f5f5f5',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#3f51b5',
          color: 'white',
          borderRadius: '8px 8px 0 0',
        }}
      >
        Print Attender Passes
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Patient: {selectedPatient?.patientName}
        </Typography>
        <Typography variant="body2" gutterBottom>
          Select up to 5 attenders to print passes for:
        </Typography>

        <List
          sx={{
            maxHeight: '300px',
            overflow: 'auto',
            bgcolor: 'white',
            borderRadius: 1,
            mt: 1,
          }}
        >
          {selectedPatient?.attenders?.map((attender) => {
            const isChecked = selectedAttenders.includes(attender.id);
            const isDisabled = !isChecked && selectedAttenders.length >= 5;
            const effectiveStatus = getEffectiveStatus(attender.id, attender.attender_status);
            const isActive = effectiveStatus === 1;

            return (
              <ListItem
                key={attender.id}
                disablePadding
                sx={{
                  borderBottom: '1px solid #e0e0e0',
                  '&:last-child': { borderBottom: 'none' },
                  bgcolor: isActive ? '#e8f5e9' : '#ffebee', // Light green for active, light red for inactive
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={(e) => {
                      if (e.target.checked) {
                        if (selectedAttenders.length < 5) {
                          setSelectedAttenders([
                            ...selectedAttenders,
                            attender.id,
                          ]);
                        }
                      } else {
                        setSelectedAttenders(
                          selectedAttenders.filter((id) => id !== attender.id)
                        );
                      }
                    }}
                    sx={{
                      color: '#3f51b5',
                      '&.Mui-checked': {
                        color: '#3f51b5',
                      },
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={attender.attender_name}
                  secondary={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography variant="body2">
                        {getRelationLabel(attender.relation)} • {attender.phone}
                      </Typography>
                      <Chip
                        label={
                          attender?.User?.Staff?.staff_id
                            ? `Staff: ${attender.User.Staff.staff_id}`
                            : 'Admin'
                        }
                        color={
                          attender?.User?.Staff?.staff_id
                            ? 'primary'
                            : 'secondary'
                        }
                        size="small"
                      />
                    </Stack>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={() => handleStatusToggle(attender.id, effectiveStatus)}
                      disabled={loadingAttenderId === attender.id}
                      color={isActive ? "success" : "error"}
                    />
                  }
                  label={loadingAttenderId === attender.id ? 
                    <CircularProgress size={16} /> : 
                    isActive ? "active " : "Inactive"
                  }
                  labelPlacement="end"
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ color: '#f44336', borderColor: '#f44336' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePrintSelected}
          variant="contained"
          sx={{
            bgcolor: '#4caf50',
            color: 'white',
            '&:hover': { bgcolor: '#388e3c' },
            '&.Mui-disabled': {
              bgcolor: '#a5d6a7',
              color: 'white',
            },
          }}
          startIcon={<Print />}
          disabled={selectedAttenders.length === 0}
        >
          Print Selected
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintAttenderDialog;